import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Loader2, AlertCircle } from 'lucide-react';
import { programsAPI, applicationsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { useAuth } from '../context/AuthContext';
import DynamicFormRenderer from '../components/DynamicFormRenderer';
import { Alert, AlertDescription } from '../components/ui/alert';

const Apply = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = translations[language].apply;
  
  // Get program ID from URL if coming from program detail page
  const programIdFromUrl = searchParams.get('program');
  
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgram, setLoadingProgram] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});

  const localT = {
    es: {
      selectProgram: 'Selecciona un Desafío',
      selectProgramPlaceholder: 'Elige un desafío para postular',
      loadingPrograms: 'Cargando desafíos...',
      noPrograms: 'No hay desafíos disponibles',
      loadingForm: 'Cargando formulario...',
      formNotConfigured: 'Este desafío aún no tiene un formulario de postulación configurado.',
      contactOrg: 'Por favor contacta a la organización para más información.',
      submitApplication: 'Enviar Postulación',
      submitting: 'Enviando...',
      applicantInfo: 'Información del Postulante',
      applicantNote: 'Tu nombre y email serán incluidos automáticamente de tu cuenta.',
      requiredFields: 'Los campos marcados con * son obligatorios',
      fillRequired: 'Por favor completa todos los campos obligatorios'
    },
    en: {
      selectProgram: 'Select a Challenge',
      selectProgramPlaceholder: 'Choose a challenge to apply',
      loadingPrograms: 'Loading challenges...',
      noPrograms: 'No challenges available',
      loadingForm: 'Loading form...',
      formNotConfigured: 'This challenge does not have an application form configured yet.',
      contactOrg: 'Please contact the organization for more information.',
      submitApplication: 'Submit Application',
      submitting: 'Submitting...',
      applicantInfo: 'Applicant Information',
      applicantNote: 'Your name and email will be automatically included from your account.',
      requiredFields: 'Fields marked with * are required',
      fillRequired: 'Please fill all required fields'
    }
  }[language];

  useEffect(() => {
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select program if coming from program detail page
  useEffect(() => {
    if (programIdFromUrl && programs.length > 0 && !selectedProgram) {
      handleProgramSelect(programIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programIdFromUrl, programs]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programsAPI.getAll({ status: 'open' });
      setPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProgramSelect = async (programId) => {
    if (!programId) {
      setSelectedProgram(null);
      setFormValues({});
      return;
    }

    try {
      setLoadingProgram(true);
      const program = await programsAPI.getById(programId);
      setSelectedProgram(program);
      
      // Initialize form values with empty values for each field
      const initialValues = {};
      if (program.customFormFields && program.customFormFields.length > 0) {
        program.customFormFields.forEach(field => {
          if (field.type === 'checkbox') {
            initialValues[field.id] = false;
          } else {
            initialValues[field.id] = '';
          }
        });
      }
      setFormValues(initialValues);
      setErrors({});
    } catch (error) {
      console.error('Error fetching program:', error);
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: language === 'es' ? 'No se pudo cargar el desafío' : 'Could not load challenge',
        variant: 'destructive'
      });
    } finally {
      setLoadingProgram(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when field is modified
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedProgram?.customFormFields) return true;
    
    // Validate vertical if program has verticals
    if (selectedProgram.verticals && selectedProgram.verticals.length > 0) {
      if (!formValues.vertical) {
        newErrors.vertical = language === 'es' ? 'Este campo es obligatorio' : 'This field is required';
      }
    }
    
    selectedProgram.customFormFields.forEach(field => {
      if (field.required) {
        const value = formValues[field.id];
        if (value === undefined || value === null || value === '' || 
            (typeof value === 'object' && !value?.url)) {
          newErrors[field.id] = language === 'es' ? 'Este campo es obligatorio' : 'This field is required';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: localT.fillRequired,
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Build submission data - combine fixed fields with custom fields
      const submitData = {
        programId: selectedProgram._id,
        // Fixed fields from user account
        founderName: user?.name || '',
        email: user?.email || '',
        // Get values from form or use defaults
        startupName: formValues.startup_name || formValues.startupName || 'N/A',
        phone: formValues.phone || formValues.telefono || 'N/A',
        website: formValues.website || formValues.sitio_web || null,
        description: formValues.description || formValues.descripcion || 'N/A',
        stage: formValues.stage || formValues.etapa || 'idea',
        teamSize: parseInt(formValues.team_size || formValues.teamSize || '1') || 1,
        fundingRaised: parseFloat(formValues.funding_raised || formValues.fundingRaised || '0') || 0,
        pitchDeck: formValues.pitch_deck?.url || formValues.pitchDeck?.url || null,
        userId: user?.id || user?._id || null,
        userEmail: user?.email || null,
        // Store all custom form data
        customFieldsData: formValues
      };
      
      // Submit to backend
      await applicationsAPI.create(submitData);

      toast({
        title: t.success.title,
        description: t.success.description,
      });

      // Redirect immediately to dashboard so applicant sees their application
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: t.error.title,
        description: error.response?.data?.detail || t.error.description,
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-black mb-4">{t.title}</h1>
          <p className="text-xl text-gray-600">{t.subtitle}</p>
        </div>

        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-2xl">{t.formTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Program Selection */}
              <div className="space-y-2">
                <Label htmlFor="programId">{localT.selectProgram} *</Label>
                <Select
                  value={selectedProgram?._id || ''}
                  onValueChange={handleProgramSelect}
                  required
                >
                  <SelectTrigger className="border-2 border-gray-200">
                    <SelectValue placeholder={localT.selectProgramPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {loading ? (
                      <SelectItem value="loading" disabled>{localT.loadingPrograms}</SelectItem>
                    ) : programs.length === 0 ? (
                      <SelectItem value="none" disabled>{localT.noPrograms}</SelectItem>
                    ) : (
                      programs.map((program) => (
                        <SelectItem key={program._id} value={program._id}>
                          {program.name} - {program.organization}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Loading state for program form */}
              {loadingProgram && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <span className="ml-3 text-gray-600">{localT.loadingForm}</span>
                </div>
              )}

              {/* Program Form */}
              {selectedProgram && !loadingProgram && (
                <>
                  {/* Applicant Info Notice */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-700">
                      <strong>{localT.applicantInfo}:</strong> {localT.applicantNote}
                      <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                        <p><strong>{language === 'es' ? 'Nombre' : 'Name'}:</strong> {user?.name}</p>
                        <p><strong>Email:</strong> {user?.email}</p>
                      </div>
                    </AlertDescription>
                  </Alert>

                  {/* Dynamic Form Fields */}
                  {selectedProgram.customFormFields && selectedProgram.customFormFields.length > 0 ? (
                    <div className="space-y-6 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 italic">{localT.requiredFields}</p>

                      {/* Auto-injected Vertical selector when program has verticals */}
                      {selectedProgram.verticals && selectedProgram.verticals.length > 0 && (
                        <div className="space-y-2">
                          <Label className="font-medium">
                            {language === 'es' ? 'Vertical / Categoría' : 'Vertical / Category'} <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formValues.vertical || ''}
                            onValueChange={(val) => handleFieldChange('vertical', val)}
                          >
                            <SelectTrigger data-testid="apply-vertical-select" className={errors.vertical ? 'border-red-500' : ''}>
                              <SelectValue placeholder={language === 'es' ? 'Selecciona una vertical...' : 'Select a vertical...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedProgram.verticals.map((v) => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.vertical && <p className="text-xs text-red-500">{errors.vertical}</p>}
                        </div>
                      )}

                      <DynamicFormRenderer
                        fields={selectedProgram.customFormFields}
                        values={formValues}
                        onChange={handleFieldChange}
                        language={language}
                        errors={errors}
                        targetStages={selectedProgram.targetStages}
                      />
                    </div>
                  ) : (
                    <Alert className="bg-yellow-50 border-yellow-200">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <AlertDescription className="text-yellow-700">
                        <strong>{localT.formNotConfigured}</strong>
                        <p className="mt-1">{localT.contactOrg}</p>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Submit Button */}
                  {selectedProgram.customFormFields && selectedProgram.customFormFields.length > 0 && (
                    <div className="pt-6">
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-black text-white hover:bg-gray-800"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {localT.submitting}
                          </>
                        ) : (
                          localT.submitApplication
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Apply;
